/**
 * Timezone-aware date utilities for the seeker app
 */

/**
 * Format a UTC ISO timestamp in a specific timezone
 */
export function formatUtcInTimezone(
  utcIso: string,
  timeZone: string,
  opts: Intl.DateTimeFormatOptions
): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, ...opts }).format(new Date(utcIso));
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    // Fallback to browser timezone
    return new Intl.DateTimeFormat('en-US', opts).format(new Date(utcIso));
  }
}

/**
 * Get the current timezone from the browser
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Error getting browser timezone:', error);
    return 'UTC';
  }
}

/**
 * Format time for display (e.g., "09:00" -> "9:00 AM")
 * This is timezone-agnostic - just formats a time string
 */
export function formatTimeForDisplay(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${displayHour}:${minutes} ${ampm}`;
}

/**
 * Format date for display (e.g., "2025-10-06" -> "October 6, 2025")
 * This is timezone-agnostic - just formats a date string
 */
export function formatDateForDisplay(date: string): string {
  const dateObj = new Date(date + 'T00:00:00'); // Add time to avoid timezone issues
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format a UTC timestamp in the seeker's timezone for display
 * Returns formatted date and time strings
 */
export function formatSessionTimeInSeekerTimezone(
  utcTimestamp: string | null | undefined,
  seekerTimezone: string | null,
  fallbackDate?: string,
  fallbackTime?: string
): { date: string; time: string; formattedDate: string; formattedTime: string } {
  // Use timezone-aware formatting if we have UTC timestamp and timezone
  if (utcTimestamp && seekerTimezone) {
    try {
      const date = formatUtcInTimezone(utcTimestamp, seekerTimezone, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const time = formatUtcInTimezone(utcTimestamp, seekerTimezone, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Reformat date from MM/DD/YYYY to YYYY-MM-DD
      const [month, day, year] = date.split('/');
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

      const formattedDate = formatDateForDisplay(isoDate);
      const formattedTime = formatTimeForDisplay(time);

      return {
        date: isoDate,
        time: time,
        formattedDate,
        formattedTime
      };
    } catch (error) {
      console.error('Error formatting session time in timezone:', error);
    }
  }

  // Fallback to legacy fields
  if (fallbackDate && fallbackTime) {
    return {
      date: fallbackDate,
      time: fallbackTime,
      formattedDate: formatDateForDisplay(fallbackDate),
      formattedTime: formatTimeForDisplay(fallbackTime)
    };
  }

  return {
    date: '',
    time: '',
    formattedDate: 'Date not available',
    formattedTime: 'Time not available'
  };
}

/**
 * Get the generic timezone abbreviation for a given UTC timestamp in a timezone
 * (e.g., "ET", "PT", "CT") — uses shortGeneric to avoid EST/EDT confusion.
 * Falls back to DST-specific abbreviation if shortGeneric is unsupported.
 */
export function getTimezoneAbbreviation(utcIso: string, timeZone: string): string {
  try {
    const date = new Date(utcIso);
    if (isNaN(date.getTime())) return '';
    // Try shortGeneric first (returns "ET" instead of "EST"/"EDT")
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortGeneric' }).formatToParts(date);
      const val = parts.find(part => part.type === 'timeZoneName')?.value;
      if (val) return val;
    } catch { /* shortGeneric not supported, fall through */ }
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(date);
    return parts.find(part => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Check if a UTC timestamp is "today" in a given timezone
 */
export function isToday(utcIso: string, timeZone: string): boolean {
  try {
    const dateInTz = formatUtcInTimezone(utcIso, timeZone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const todayInTz = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    return dateInTz === todayInTz;
  } catch {
    return false;
  }
}

/**
 * Check if a UTC timestamp is "tomorrow" in a given timezone
 */
export function isTomorrow(utcIso: string, timeZone: string): boolean {
  try {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateInTz = formatUtcInTimezone(utcIso, timeZone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const tomorrowInTz = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(tomorrow);
    return dateInTz === tomorrowInTz;
  } catch {
    return false;
  }
}

/**
 * Format time in a specific timezone (e.g., "3:00 PM")
 */
export function formatTimeInTimezone(utcIso: string, timeZone: string): string {
  return formatUtcInTimezone(utcIso, timeZone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a short date in a specific timezone (e.g., "Jan 6")
 */
export function formatShortDateInTimezone(utcIso: string, timeZone: string): string {
  return formatUtcInTimezone(utcIso, timeZone, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Check if a slot is in the past (used for filtering available slots)
 */
export function isSlotInPast(
  startUtc: string | undefined,
  legacyDate: string,
  legacyTime: string
): boolean {
  const now = new Date();
  const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);

  if (startUtc) {
    // Use UTC timestamp if available
    return new Date(startUtc) <= twoHoursFromNow;
  } else {
    // Fallback to legacy date/time (assumes UTC)
    const slotDateTime = new Date(`${legacyDate}T${legacyTime}:00Z`);
    return slotDateTime <= twoHoursFromNow;
  }
}
